import { useState } from "react";
import { LayoutDashboard, CreditCard, Wallet, CalendarRange, Eye, GitCompare, ChevronLeft, ChevronRight, LogOut, Menu, Settings, TrendingUp } from "lucide-react";

const navItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "debt-tracker", label: "Debt Tracker", icon: CreditCard },
  { id: "monthly-budget", label: "Monthly Budget", icon: Wallet },
  { id: "payoff-plan", label: "Payoff Plan", icon: CalendarRange },
  { id: "payment-view", label: "Payment View", icon: Eye },
  { id: "strategy", label: "Snowball vs Avalanche", icon: GitCompare },
  { id: "day-trading", label: "Day Trading", icon: TrendingUp },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function Sidebar({ activeView, onNavigate, user, onLogout, collapsed, onToggle }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNav = (id) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile hamburger */}
      <button
        data-testid="sidebar-mobile-toggle"
        className="fixed top-3 left-3 z-50 md:hidden p-2 bg-white border border-gray-200 rounded-sm shadow-sm"
        onClick={() => setMobileOpen(true)}
      >
        <Menu className="w-5 h-5 text-gray-700" strokeWidth={1.5} />
      </button>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-gray-200 flex flex-col shadow-lg">
            <SidebarContent activeView={activeView} onNavigate={handleNav} user={user} onLogout={onLogout} collapsed={false} />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className={`hidden md:flex relative h-full bg-white border-r border-gray-200 flex-col shrink-0 transition-all duration-200 ease-in-out ${collapsed ? "w-16" : "w-64"}`}
      >
        <button
          data-testid="sidebar-toggle"
          onClick={onToggle}
          className="absolute -right-3 top-7 z-50 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} /> : <ChevronLeft className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />}
        </button>
        <SidebarContent activeView={activeView} onNavigate={onNavigate} user={user} onLogout={onLogout} collapsed={collapsed} />
      </aside>
    </>
  );
}

function SidebarContent({ activeView, onNavigate, user, onLogout, collapsed }) {
  return (
    <>
      <div className={`border-b border-gray-200 ${collapsed ? "p-3 flex items-center justify-center" : "p-6"}`}>
        {collapsed ? (
          <span className="font-heading text-sm font-bold tracking-tighter text-black uppercase">TV</span>
        ) : (
          <>
            <h1 className="font-heading text-xl font-bold tracking-tighter text-black uppercase">The Vault</h1>
            <p className="text-xs text-gray-500 mt-1 font-mono-data">Budget & Debt Tracker</p>
          </>
        )}
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => onNavigate(item.id)}
              title={collapsed ? item.label : undefined}
              className={`w-full flex items-center transition-colors duration-150
                ${collapsed ? "justify-center px-0 py-3" : "gap-3 px-6 py-2.5"}
                ${isActive ? "bg-gray-100 text-black font-medium border-r-2 border-black" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"} text-sm`}
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      <div className={`border-t border-gray-200 ${collapsed ? "p-2 flex justify-center" : "p-4"}`}>
        {user && (
          collapsed ? (
            <button data-testid="logout-btn" onClick={onLogout} className="p-1.5 hover:bg-gray-100 rounded-sm transition-colors" title="Sign out">
              <LogOut className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name || user.email}</p>
                  <p className="text-xs text-gray-400 truncate">{user.email}</p>
                </div>
                <button data-testid="logout-btn" onClick={onLogout} className="p-1.5 hover:bg-gray-100 rounded-sm transition-colors shrink-0 ml-2" title="Sign out">
                  <LogOut className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                </button>
              </div>
              {/* "Switch app" — clears the remembered workspace and reloads,
                  surfacing the post-login app selector again. Lets users in
                  the wrong app get back out without signing out. */}
              <button
                data-testid="switch-app-btn"
                onClick={() => {
                  try { localStorage.removeItem("vault_preferred_app"); } catch { /* noop */ }
                  window.location.reload();
                }}
                style={{ fontSize: 11, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", padding: "4px 8px", marginTop: 4 }}
              >
                Switch app
              </button>
            </div>
          )
        )}
      </div>
    </>
  );
}
